import { forwardRef, useContext, useEffect, useRef, useState } from 'react';

import { ApplicationContext } from '@src/app/contexts/Application';

import BackButton from '../../components/BackButton/BackButton';
import ProgressMenu from '../../components/Progress/ProgressMenu';

import ApplicantView from './ApplicantView';

interface ApplicantsViewProps {
    applicantsData?: any;
    activeApplicant?: number | null;
    saveApplicants: (
        data: any,
        isPrimaryApplicant: boolean,
        noOfApplicants: number,
    ) => Promise<any>;
}

const ApplicantsView = forwardRef((props: ApplicantsViewProps, ref) => {
    const { applicationEmail } = useContext(ApplicationContext);

    const [activeApplicant, setActiveApplicant] = useState(
        props.activeApplicant || 0,
    );

    const [applicantCount, setApplicantCount] = useState(1);

    const topRef = useRef<HTMLDivElement>(null);
    const firstViewRef = useRef(null);
    const secondViewRef = useRef(null);
    const [isFirst, setIsFirst] = useState(false);
    const [isSecond, setIsSecond] = useState(false);

    const currentPath = window.location.pathname;

    useEffect(() => {
        if (
            props.applicantsData?.[0].hasSecondApplicant === 'true' ||
            currentPath === '/eligibility/applicants2'
        ) {
            setApplicantCount(2);
            setActiveApplicant(1);
        } else {
            setApplicantCount(1);
            setActiveApplicant(0);
        }
    }, [props.applicantsData, currentPath]);

    useEffect(() => {
        if (props.activeApplicant) {
            setActiveApplicant(props.activeApplicant);
            topRef.current?.scrollIntoView({ behavior: 'smooth' });
        }
    }, [props.activeApplicant]);

    useEffect(() => {
        if (currentPath === '/eligibility/applicants') {
            setIsFirst(true);
            setIsSecond(false);
        } else {
            setIsFirst(false);
            setIsSecond(true);
        }
    }, [currentPath, activeApplicant, applicantCount]);

    const commonProps = {
        saveRef: ref,
        saveApplicants: props.saveApplicants,
        applicantCount: applicantCount,
        setActiveApplicant: setActiveApplicant,
    };

    return (
        <div className='flex flex-row justify-between' ref={topRef}>
            <div className='w-full sm:w-1/2 lg:w-2/5 mt-20 sm:mt-0 sm:mr-10'>
                <div className='hidden lg:block mb-2'>
                    <BackButton />
                </div>
                <div className='text-2xl lg:text-4xl font-sora-bold tracking-tighter'>
                    {isFirst && <p>About you</p>}
                    {isSecond && <p>Applicant 2</p>}
                </div>

                <div className='relative mb-15'>
                    {isFirst && (
                        <ApplicantView
                            {...commonProps}
                            formRef={(ref: any) => {
                                firstViewRef.current = ref;
                            }}
                            isPrimaryApplicant={true}
                            applicantData={props.applicantsData?.[0] || {}}
                            setApplicantCount={setApplicantCount}
                        />
                    )}
                    {applicantCount === 2 && isSecond && (
                        <ApplicantView
                            {...commonProps}
                            formRef={(ref: any) =>
                                (secondViewRef.current = ref)
                            }
                            isPrimaryApplicant={false}
                            applicantData={props.applicantsData?.[1] || {}}
                            mobilePhoneNumber={
                                props.applicantsData?.[0].mobilePhoneNumber
                            }
                            emailAddress={applicationEmail}
                        />
                    )}
                </div>
            </div>
            <ProgressMenu />
        </div>
    );
});

export default ApplicantsView;
